import {
  Box,
  Button,
  Card,
  CircularProgress,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import InfoIcon from "@mui/icons-material/Info";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Url } from "../../Api/Api";
import { getAccessToken } from "../../hooks/useLocalStorage";
import Swal from "sweetalert2";
import noDataFolder from "../../Assets/folder.png";
import { DownloadSvgIcon } from "../../Assets/Icon_Svg_Format/Admin_Icon_Svg_Format";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { CategoryTableRowSkeleton } from "../../components/adminComponents/SimulationActivity";

const Category = () => {
  const theme = useTheme();
  const { drawerOpen } = useOutletContext();
  const drawerWidth = 10;
  const collapsedWidth = 240;

  const token = getAccessToken();

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#51b0cfff",
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  const [loading, setLoading] = useState("");
  // const [isDelete, setIsDelete] = useState();

  const [categoryData, setCategoryData] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);

  const fetchCategoryData = async () => {
    setCategoryLoading(true);
    try {
      const response = await axios.get(Url.GetCategoryData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCategoryData(
        response?.data?.map((item) => ({
          categoryName: item.categoryName,
        })) || [],
      );
    } catch (e) {
      console.log(e, "error");
    } finally {
      setCategoryLoading(false);
    }
  };

  useEffect(() => {
    fetchCategoryData();
  }, []);

  const [excelLoader, setExcelLoader] = useState(false);

  const DownloadExcel = async () => {
    try {
      setExcelLoader(true);

      const responce = await axios.get(Url.DownloadCategory, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });

      // Trigger file download
      const fileURL = window.URL.createObjectURL(new Blob([responce.data]));
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", "StudentDetails.xlsx");
      document.body.appendChild(link);
      link.click();

      Swal.fire({
        title: "Success",
        text: "Excel Downloaded Successfully",
        icon: "success",
        draggable: false,
        iconColor: "#00BFFF",
        confirmButtonColor: "#80278C",
      });
    } catch (e) {
      Swal.fire("Error", "Unable to download file", "error");
    } finally {
      setExcelLoader(false);
    }
  };

  const [uploadLoader, setUploadLoader] = useState(false);

  const uploadExcel = async (selectedFile) => {
    setUploadLoader(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const res = await axios.post(Url.UploadCategory, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      await fetchCategoryData();
      // await Get_Simulation_Group_and_Student();

      // SUCCESS RESPONSE
      Swal.fire({
        title: "Upload Successful",
        text: res?.data?.message,
        icon: "success",
        iconColor: "#51b0cf",
        confirmButtonColor: "#80278C",
      });
    } catch (err) {
      console.error(err);

      // HANDLE 401 specifically
      if (err?.response?.status === 401) {
        Swal.fire({
          title: "Authentication Failed",
          text: "Please log in again.",
          icon: "error",
          confirmButtonColor: "#80278C",
        });
        return;
      }

      Swal.fire({
        title: "Upload Failed",
        text: err.response?.data?.detail || "An unexpected error occurred.",
        icon: "warning",
        confirmButtonColor: "#80278C",
      });
    } finally {
      setUploadLoader(false);
    }
  };

  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    setFile(selected);
    uploadExcel(selected); // auto-upload
  };

  const TableDataStyles = {
    fontSize: "15px",
    color: "#002147",
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          marginTop: "0px",
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: `${drawerOpen ? drawerWidth : collapsedWidth}px`,
          ...(!drawerOpen && {
            transition: theme.transitions.create("margin", {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
        >
          {/* Left Section */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{
                background:
                  theme.palette.mode === "light"
                    ? "linear-gradient(to right, #00bfff, #00bfff)"
                    : "linear-gradient(to right, #00bfff, #00bfff)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: "inline-block",
              }}
            >
              Category Management
            </Typography>
            <Typography variant="body1" color="#002147" mb={1}>
              Set up and organize category names efficiently.
            </Typography>
          </Box>

          {/* Right Section - Button */}
        </Stack>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          spacing={1}
          mb={2}
          mt={1}
        >
          {/* Left side - Note box */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#e6f3f7",
              borderLeft: "4px solid #51b0cfff",
              padding: "8px 16px",
              borderRadius: "4px",
              gap: 1,
            }}
          >
            <InfoIcon sx={{ color: "#51b0cfff", fontSize: 20 }} />
            <Typography
              component="span"
              sx={{
                color: "#51b0cfff",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              Note:
            </Typography>
            <Typography
              sx={{
                color: "#2c3e50",
                fontSize: "14px",
              }}
            >
              Download and use this Excel file only to add, edit and manage
              category actions.
            </Typography>
          </Box>

          {/* Right side - Buttons */}
          <Stack direction={"row"} spacing={2.5}>
            <Button
              startIcon={
                excelLoader ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DownloadSvgIcon />
                )
              }
              disabled={excelLoader}
              onClick={DownloadExcel}
              size="small"
              variant="contained"
              sx={{
                backgroundColor: "#51b0cfff",
                height: 35,
                ":hover": { background: "#51b0cfff", color: "#fff" },
                whiteSpace: "nowrap",
              }}
            >
              Download Excel
            </Button>
            <input
              type="file"
              accept=".xls,.xlsx"
              style={{ display: "none" }}
              id="excel-upload"
              onChange={handleFileChange}
            />
            <Button
              variant="contained"
              disabled={uploadLoader}
              size="small"
              startIcon={
                uploadLoader ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FileUploadIcon />
                )
              }
              sx={{
                backgroundColor: "#51b0cfff",
                ":hover": { background: "#51b0cfff", color: "#fff" },
              }}
              onClick={() => document.getElementById("excel-upload").click()}
            >
              Upload Excel
            </Button>
          </Stack>
        </Stack>

        <Card sx={{ p: 3 }}>
          <TableContainer
            sx={{ height: 500, scrollbarWidth: "thin" }}
            component={Paper}
          >
            <Table
              stickyHeader
              sx={{ minWidth: "100%" }}
              aria-label="customized table"
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>S.No</StyledTableCell>
                  <StyledTableCell align="left">Category Name</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categoryLoading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <CategoryTableRowSkeleton key={i} />
                  ))
                ) : !categoryData.length ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Stack
                        minHeight={400}
                        textAlign="center"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <img
                          src={noDataFolder}
                          height={"100px"}
                          width={"100px"}
                          alt="img"
                        />
                        <Typography sx={TableDataStyles}>
                          No data found
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : (
                  categoryData?.map((row, index) => (
                    <StyledTableRow key={row.name}>
                      <StyledTableCell
                        component="th"
                        // scope="row"
                        sx={TableDataStyles}
                      >
                        &nbsp;&nbsp;&nbsp;{index + 1}
                      </StyledTableCell>
                      <StyledTableCell align="left" sx={TableDataStyles}>
                        {row.categoryName}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </Box>
  );
};

export default Category;
